<!-- Root mount point for the shop area -->
<!-- Unfortunately, this uses a lot of global css from src/scss/pos/_shop-area-base.scss -->

<template>
  <div
    v-if="shopBootstrapped"
    class="shop-area"
    :class="{ 'is-fullscreen': fullScreen }"
  >
    <ShopSetupWizard v-if="showShopSetupWizard" />
    <DetailPage v-else full-width heading="SA.navigation.editShop.label">
      <template #left-nav>
        <button class="link link-main icon-link" @click="back">
          <Icon icon="close" />
          {{ $t('GENERAL.CLOSE') }}
        </button>
      </template>
      <template #right-nav> </template>
      <template #content>
        <div class="row content-wrapper">
          <ShopNavigation @navigation-update="onNavigationUpdate" />

          <div
            class="context"
            :class="{
              'is-fullscreen': fullScreen,
              'no-livepreview': !livePreviewVisible,
            }"
          >
            <ActivationBar v-if="settings.hidden" />
            <router-view
              class="settings"
              :class="{ hidden: livePreviewVisible }"
              name="context@shop-area"
            ></router-view>
            <ShopActionBar />
            <ShopLivePreview />
          </div>
        </div>
      </template>
    </DetailPage>
  </div>
</template>

<script>
import ActivationBar from './activation/ActivationBar.vue';
import ShopActionBar from './actionBar/ShopActionBar.vue';
import ShopLivePreview from './livePreview/ShopLivePreview.vue';
import ShopNavigation from './navigation/ShopNavigation.vue';
import ShopSetupWizard from './setupWizard/ShopSetupWizard.vue';
import { SettingsData } from 'src/app/partnerarea/pos/shop/shopSavable/SettingsData';
import unsavedChangesService from 'commons/unsavedChangesService/unsavedChangesService';
import dialogService from 'commons/dialogs/wrapper/dialogService';
import ShopActivationDialog from 'src/app/partnerarea/pos/shop/activation/ActivationDialog.vue';
import { shopAPI } from 'commons/api/shop/shopAPI.js';
import DetailPage from 'src/app/components/detailPage/DetailPage.vue';

const newStorefrontRestrictedRoutes = [
  'shop-area.pages.collections',
  'shop-area.pages.collections-detail',
  'shop-area.pages.detail-page',
  'shop-area.design.templates',
  'shop-area.marketing.social-media-tool',
  'shop-area.design.promotion-appearance',
];

export default {
  name: 'ShopArea',
  components: {
    ActivationBar,
    ShopActionBar,
    ShopLivePreview,
    ShopNavigation,
    ShopSetupWizard,
    DetailPage,
  },
  mixins: [SettingsData],
  beforeRouteUpdate(to, from, next) {
    if (this.newStorefront && newStorefrontRestrictedRoutes.includes(to.name)) {
      return next({ name: 'shop-area.staticShopPreview' });
    }

    if (this.customSaving) {
      return next();
    }

    if (this.$store.getters['shop/shopSavableDirty']) {
      return next(false);
    }

    if (to.params.shopId !== from.params.shopId) {
      // switching shops
      this.beforeShopAreaLeave(to, from, next);
    } else {
      // shop area internal page routing — only validate if data was actually changed
      if (this.$store.getters['shop/shopSavableDirty']) {
        this.$store.commit('shop/validateShopSavableSubset', from.name);
        if (
          !from.params.skipValidation &&
          this.$store.getters['shop/hasShopSavableValidationErrors']
        ) {
          return false;
        }
      }
      next();
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.customSaving) {
      return next();
    }

    this.beforeShopAreaLeave(to, from, next);
  },
  data() {
    return {
      shopBootstrapped: false,
      customSaving: false,
      previousState: null,
    };
  },
  computed: {
    fullScreen() {
      return this.$store.state.shop.livePreview.fullScreen;
    },
    livePreviewVisible() {
      return this.$store.state.shop.livePreview.visible;
    },
    showShopSetupWizard() {
      // we check the server state here to prevent a race condition on saving the wizard changes.
      // (the live preview component must not be remounted after the changes have arrived on the server side.)
      return !this.$store.state.shop.shopSavable.originalData.settings
        .setupWizardDone;
    },
  },
  watch: {
    '$route.params.shopId': {
      handler(shopId, oldShopId) {
        if (shopId && shopId !== oldShopId) {
          this.init();
        }
      },
      deep: true,
    },
  },
  created() {
    this.previousState = this.$route.meta.previousState;
    this.init();
  },
  methods: {
    init() {
      this.initCurrentShop();

      if (this.$route.name === 'shop-area') {
        this.$router.push({ name: 'shop-area.staticShopPreview' });
      }
    },
    async initCurrentShop() {
      await this.$store.dispatch('shop/bootstrapShopSavable', {
        userId: this.$store.state.user.data.id,
        shopId: this.$route.params.shopId,
      });

      if (SPRD.PLATFORM?.toUpperCase() === 'NA') {
        this.$store.dispatch('shop/getCustomDomain');
      }

      if (
        this.newStorefront &&
        newStorefrontRestrictedRoutes.includes(this.$route.name)
      ) {
        this.$router.replace({ name: 'shop-area.staticShopPreview' });
      }

      this.shopBootstrapped = true;
    },
    async deactivatedShopReminder(to, from, next) {
      try {
        const data = await shopAPI.getShop(
          this.$store.state.user.data.id,
          from.params.shopId,
        );
        if (
          !from.meta.hasUnsavedComponents &&
          data &&
          data.hidden &&
          !this.$store.getters['shop/isShopEmpty']
        ) {
          await dialogService.openDialog(ShopActivationDialog);
        } else if (!from.meta.hasUnsavedComponents) {
          next();
        }
      } catch {
        next();
      }
    },
    beforeShopAreaLeave(to, from, next) {
      if (this.$store.getters['shop/shopSavableDirty']) {
        this.$store.commit('shop/validateShopSavableSubset', from.name);
      }
      unsavedChangesService.triggerCheck({
        check: () => this.$store.getters['shop/shopSavableDirty'],
        save: () => {
          if (!this.$store.getters['shop/hasShopSavableValidationErrors']) {
            this.$store.dispatch('shop/saveShopSavable').then(() => {
              this.deactivatedShopReminder(to, from, next);
            });
          }
        },
        cancel: () => {
          this.$store.commit('shop/resetShopSavable');
          this.deactivatedShopReminder(to, from, next);
        },
        unchanged: () => {
          this.deactivatedShopReminder(to, from, next);
        },
      });
    },
    onNavigationUpdate(navigation) {
      this.customSaving = !!navigation?.customSaving;
    },
    back() {
      this.$router.push({
        name: this.previousState?.name || 'partnerarea.dashboard',
        params: this.previousState?.params,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/scss/utils';

.hidden {
  display: none;
  height: 0;
}
</style>

<style lang="scss">
@import 'src/scss/styleguide/colors';

#application.state-shop-area {
  overflow-y: initial;

  .navbar-main {
    display: none;
  }

  #main-content {
    overflow: initial;
    margin-left: 0;
  }
}
</style>
